import React, { useEffect, useRef, useState } from 'react';
import { styles } from './Wings.css';
import { useTrackingContext } from '../Tracking';
import { Link } from '../PlatformFunctionality';

interface Wing {
  name: string;
  brand?: string;
  destinationUrl: string;
  image: React.ReactNode;
}

export interface WingsProps {
  leftWing?: Wing;
  rightWing?: Wing;
  trackingData?: {
    campaignName?: string;
    campaignStartDate?: string;
    category?: string;
    isBranded?: boolean;
  };
}

const HEADER_HEIGHT = 112;

export const Wings = ({ leftWing, rightWing, trackingData }: WingsProps) => {
  const divRef = useRef<HTMLDivElement>(null);
  const leftWingRef = useRef<HTMLAnchorElement>(null);
  const [isFixed, setIsFixed] = useState(false);
  const { tracker } = useTrackingContext();

  useEffect(() => {
    tracker.trackCustomEvent({ event: 'internal promotion impression', gaipElement: leftWingRef.current });

    const handleScroll = () => {
      if (divRef.current) {
        const divTop = divRef.current.getBoundingClientRect().top;
        setIsFixed(divTop < HEADER_HEIGHT);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const trackingDataToDataAttributes = (wing: Wing) => {
    if (!trackingData) return {};
    return {
      'data-gaip': 'true',
      'data-gaip-campaign': trackingData.campaignName ?? '',
      'data-gaip-category': trackingData.category ?? '',
      'data-gaip-brand': wing.brand ?? '',
      'data-gaip-type': 'wing',
      'data-gaip-position': '1',
      'data-gaip-has-onclick': 'true',
      'data-gaip-url': wing.destinationUrl ?? '',
      'data-gaip-id': wing.name ?? '',
      'data-testid': 'collection-ad-wing',
      'data-gaip-campaign-start-date': trackingData.campaignStartDate ?? '',
      'data-gaip-is-branded': trackingData.isBranded ?? false,
    };
  };

  const handleClick: React.MouseEventHandler<HTMLAnchorElement> = e => {
    tracker.trackCustomEvent({ event: 'internal promotion click', gaipElement: e.currentTarget });
  };

  return (
    <div ref={divRef} className={styles.container}>
      {leftWing ? (
        <div className={styles.wings({ side: 'left', fixed: isFixed })}>
          <Link
            onClick={handleClick}
            href={leftWing.destinationUrl}
            ref={leftWingRef}
            {...trackingDataToDataAttributes(leftWing)}
          >
            {leftWing.image}
          </Link>
        </div>
      ) : null}
      {rightWing ? (
        <div className={styles.wings({ side: 'right', fixed: isFixed })}>
          <Link onClick={handleClick} href={rightWing.destinationUrl} {...trackingDataToDataAttributes(rightWing)}>
            {rightWing.image}
          </Link>
        </div>
      ) : null}
    </div>
  );
};
